import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Home,
  MessageSquare,
  DollarSign,
  AlertCircle,
  Star,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Notification, NotificationType } from "@/types/types";

interface NotificationListProps {
  notifications: Notification[];
  onMarkAsRead: (notificationId: string) => void;
  onClose: () => void;
}

export function NotificationList({
  notifications,
  onMarkAsRead,
  onClose,
}: NotificationListProps) {
  const navigate = useNavigate();

  const getNotificationIcon = (type: NotificationType) => {
    const iconClass = "h-5 w-5";
    switch (type) {
      case "application_submitted":
      case "application_approved":
      case "application_rejected":
        return <FileText className={iconClass} />;
      case "property_approved":
      case "property_rejected":
        return <Home className={iconClass} />;
      case "message_received":
        return <MessageSquare className={iconClass} />;
      case "payment_received":
      case "payment_due":
      case "payment_overdue":
        return <DollarSign className={iconClass} />;
      case "review_received":
        return <Star className={iconClass} />;
      case "lease_created":
      case "lease_expiring":
      case "lease_renewed":
        return <FileText className={iconClass} />;
      case "maintenance_requested":
      case "maintenance_updated":
      case "maintenance_completed":
        return <AlertCircle className={iconClass} />;
      default:
        return <AlertCircle className={iconClass} />;
    }
  };

  const getNotificationColor = (type: NotificationType) => {
    switch (type) {
      case "property_approved":
      case "payment_received":
      case "application_approved":
      case "maintenance_completed":
        return "text-green-600";
      case "property_rejected":
      case "payment_overdue":
      case "application_rejected":
        return "text-red-600";
      case "payment_due":
      case "lease_expiring":
        return "text-yellow-600";
      case "message_received":
        return "text-blue-600";
      case "review_received":
        return "text-purple-600";
      default:
        return "text-gray-600";
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      onMarkAsRead(notification.id);
    }
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      onClose();
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diffInSeconds = Math.floor(
      (now.getTime() - notificationTime.getTime()) / 1000
    );

    if (diffInSeconds < 60) {
      return "Just now";
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h ago`;
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}d ago`;
    } else {
      return notificationTime.toLocaleDateString();
    }
  };

  return (
    <div className="divide-y">
      {notifications.map((notification) => (
        <div
          key={notification.id}
          className={cn(
            "flex gap-3 p-4 hover:bg-accent cursor-pointer transition-colors",
            !notification.read && "bg-blue-50/50 dark:bg-blue-950/20"
          )}
          onClick={() => handleNotificationClick(notification)}
        >
          <div
            className={cn(
              "flex-shrink-0 mt-0.5",
              getNotificationColor(notification.type)
            )}
          >
            {getNotificationIcon(notification.type)}
          </div>
          <div className="flex-1 space-y-1 min-w-0">
            <p
              className={cn(
                "text-sm leading-tight",
                !notification.read && "font-semibold"
              )}
            >
              {notification.title}
            </p>
            <p className="text-xs text-muted-foreground line-clamp-2">
              {notification.message}
            </p>
            <p className="text-xs text-muted-foreground">
              {formatTimeAgo(notification.createdAt)}
            </p>
          </div>
          {!notification.read && (
            <div className="flex-shrink-0">
              <div className="h-2 w-2 rounded-full bg-blue-600" />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
